import { type FC, type RefObject, useEffect, useState, useRef, useCallback } from 'react';
import { PinchZoom } from './PinchZoom.jsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import ZoomOut from 'dibs-icons/exports/legacy/ZoomOut.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import styles from './styles/PinchZoomMobile.scss';

const PinchZoomMobile: FC<{
    pinchZoomEnabled: boolean;
    isItemZoomedIn: boolean;
    photoPath: string;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onLoad?: () => void;
    className?: string;
    hideZoomOutButton?: boolean;
    zoomModelRef?: RefObject<$TSFixMe>;
}> = ({
    pinchZoomEnabled,
    isItemZoomedIn,
    photoPath,
    onZoomIn,
    onZoomOut,
    onLoad,
    className,
    hideZoomOutButton = false,
    zoomModelRef: zoomModelRefProp,
}) => {
    const [loaded, setLoaded] = useState(false);
    const internalZoomModelRef = useRef<$TSFixMe>(null);
    const zoomModelRef = zoomModelRefProp ?? internalZoomModelRef;

    useEffect(() => {
        const img = new Image();
        let didLoad = false;
        const handleLoad = (): void => {
            if (didLoad) {
                return;
            }
            didLoad = true;
            setLoaded(true);
            onLoad?.();
        };
        img.onload = handleLoad;
        img.src = photoPath;

        if (img.complete) {
            handleLoad();
        }

        return () => {
            img.onload = null;
        };
    }, [photoPath, onLoad]);

    const onClick = useCallback(() => {
        zoomModelRef?.current?.reset();
        onZoomOut();
    }, [onZoomOut, zoomModelRef]);

    const style = { backgroundImage: `url("${photoPath}")` };

    if (!loaded) {
        return (
            <div
                className={classnames(
                    dibsCss.relative,
                    dibsCss.flex1,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    styles.pinchZoomContainer,
                    className
                )}
            >
                <Spinner dark />
            </div>
        );
    }

    return (
        <div
            className={classnames(
                dibsCss.relative,
                dibsCss.flex1,
                dibsCss.flex,
                styles.pinchZoomContainer,
                className
            )}
        >
            {isItemZoomedIn && !hideZoomOutButton && (
                <Link
                    className={classnames(
                        styles.zoomOut,
                        dibsCss.zBump,
                        dibsCss.absolute,
                        dibsCss.bottomXsmall,
                        dibsCss.rightXsmall,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        dibsCss.roundedFull
                    )}
                    onClick={onClick}
                >
                    <ZoomOut className={styles.zoomOutIcon} />
                </Link>
            )}
            <PinchZoom
                zoomModelRef={zoomModelRef}
                className={classnames(styles.zoomImageWrapper, dibsCss.flex1, dibsCss.flex)}
                enabled={pinchZoomEnabled}
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                maxZoom={3}
            >
                <div className={classnames(styles.zoomImage, dibsCss.flex1)} style={style} />
            </PinchZoom>
        </div>
    );
};

export { PinchZoomMobile };
